// La bàn Bát Trạch: mặt la kinh nhiều vành (độ · du niên · 24 sơn · 8 hướng ·
// bát quái) xoay theo cảm biến điện thoại, kim cố định ở đỉnh chỉ hướng người
// dùng đang quay mặt tới. Máy không có cảm biến (desktop) thì kéo để xoay.
import { useCallback, useMemo, useRef, useState } from "react";
import { Compass, Hand, LocateFixed, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { useCompassHeading } from "@/hooks/use-compass-heading";
import { ChuCung } from "@/components/la-ban-shared";
import { C, GHI_CHU_TRANG_THAI, mauNen, toXY, vanhQuat } from "@/lib/la-ban-geometry";
import {
  DU_NIEN,
  DU_NIEN_META,
  HUONG_8,
  SON_24,
  chuanHoaGoc,
  huongTuGoc,
  luanTamHuong,
  nhomMenh,
  sonTuGoc,
  type Quai,
} from "@/lib/bat-trach";

// Bán kính từng vành, từ ngoài vào trong.
const R_DO_NGOAI = 198;
const R_DO_TRONG = 178;
const R_DU_NIEN_NGOAI = 176;
const R_DU_NIEN_TRONG = 144;
const R_SON_NGOAI = 142;
const R_SON_TRONG = 114;
const R_HUONG_NGOAI = 112;
const R_HUONG_TRONG = 84;
const R_QUAI_NGOAI = 82;
const R_QUAI_TRONG = 58;

export function LaBanBatTrach({ menh, canCu }: { menh: Quai; canCu?: string }) {
  const { heading, trangThai, hoTro, batLaBan, tatLaBan } = useCompassHeading();
  const [gocKeo, setGocKeo] = useState(0);
  const keo = useRef<{ gocBatDau: number; gocKeoBatDau: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const dungCamBien = heading !== null;
  const gocHienTai = chuanHoaGoc(dungCamBien ? heading : gocKeo);

  const huong = huongTuGoc(gocHienTai);
  const son = sonTuGoc(gocHienTai);
  const gocToa = chuanHoaGoc(gocHienTai + 180);
  const huongToa = huongTuGoc(gocToa);

  const luan = useMemo(() => luanTamHuong(menh), [menh]);
  const luanTheoTen = useMemo(() => new Map(luan.map((l) => [l.ten, l])), [luan]);
  const huongDangNhin = luanTheoTen.get(huong)!;
  const huongToaLuan = luanTheoTen.get(huongToa)!;

  // ── Kéo để xoay (máy không có cảm biến) ────────────────────────────────────
  const gocConTro = useCallback((e: React.PointerEvent) => {
    const o = svgRef.current?.getBoundingClientRect();
    if (!o) return 0;
    const dx = e.clientX - (o.left + o.width / 2);
    const dy = e.clientY - (o.top + o.height / 2);
    return chuanHoaGoc((Math.atan2(dx, -dy) * 180) / Math.PI);
  }, []);

  const batDauKeo = (e: React.PointerEvent) => {
    if (dungCamBien) return;
    keo.current = { gocBatDau: gocConTro(e), gocKeoBatDau: gocKeo };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const dangKeo = (e: React.PointerEvent) => {
    if (!keo.current) return;
    const lech = gocConTro(e) - keo.current.gocBatDau;
    setGocKeo(chuanHoaGoc(keo.current.gocKeoBatDau - lech));
  };

  const ketThucKeo = (e: React.PointerEvent) => {
    if (!keo.current) return;
    keo.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div>
      {/* Số đo hướng. Các ô đều cố định bề rộng: số độ đổi liên tục, nếu để co
          giãn theo nội dung thì cả dòng sẽ giật sang trái phải mỗi khung hình. */}
      <div className="flex items-baseline justify-center gap-2">
        <span className="w-[3.5ch] text-right font-display text-2xl font-bold tabular-nums text-foreground sm:text-3xl">
          {Math.round(gocHienTai)}
        </span>
        <span className="font-display text-2xl font-bold text-muted-foreground sm:text-3xl">°</span>
        <span className="w-[8.5ch] font-display text-2xl font-bold text-primary sm:text-3xl">
          {huong}
        </span>
      </div>
      <div className="mt-1.5 flex justify-center">
        <span
          className="rounded-full px-2.5 py-1 text-[11px] font-bold"
          style={{
            background: mauNen(huongDangNhin.tot, 0.18),
            color: `var(--element-${huongDangNhin.tot ? "wood" : "fire"})`,
          }}
        >
          {huongDangNhin.duNien} · {huongDangNhin.muc}
        </span>
      </div>
      <p className="mt-1.5 text-center text-[11px] tabular-nums text-muted-foreground">
        Sơn {son} · Toạ {Math.round(gocToa)}° {huongToa} ({huongToaLuan.duNien})
      </p>

      <svg
        ref={svgRef}
        viewBox="0 0 400 400"
        className={cn(
          "mx-auto mt-3 w-full max-w-[380px] touch-none select-none",
          !dungCamBien && "cursor-grab active:cursor-grabbing",
        )}
        onPointerDown={batDauKeo}
        onPointerMove={dangKeo}
        onPointerUp={ketThucKeo}
        onPointerCancel={ketThucKeo}
        role="img"
        aria-label={`La bàn Bát Trạch cung ${menh}. Đang hướng ${huong}, ${huongDangNhin.duNien}.`}
      >
        {/* ── Mặt la bàn: xoay ngược góc hướng để vành Bắc bám bắc thật ── */}
        <g transform={`rotate(${-gocHienTai} ${C} ${C})`}>
          <circle
            cx={C}
            cy={C}
            r={R_DO_NGOAI}
            className="fill-card stroke-border"
            strokeWidth={1}
          />

          {/* Vành độ */}
          {Array.from({ length: 72 }, (_, i) => i * 5).map((g) => {
            const chinh = g % 15 === 0;
            const [x1, y1] = toXY(g, R_DO_NGOAI - 1);
            const [x2, y2] = toXY(g, R_DO_NGOAI - (chinh ? 9 : 5));
            return (
              <line
                key={g}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                className={chinh ? "stroke-foreground/45" : "stroke-border"}
                strokeWidth={chinh ? 1.1 : 0.7}
              />
            );
          })}
          {Array.from({ length: 12 }, (_, i) => i * 30).map((g) => (
            <ChuCung
              key={g}
              id={`do-${g}`}
              r={(R_DO_NGOAI + R_DO_TRONG) / 2 - 1}
              goc={g}
              doRong={26}
              className="fill-muted-foreground text-[9px] font-semibold tabular-nums"
            >
              {g}
            </ChuCung>
          ))}
          <circle
            cx={C}
            cy={C}
            r={R_DO_TRONG}
            className="fill-none stroke-border"
            strokeWidth={0.8}
          />

          {/* Vành du niên — 8 cung 45° */}
          {luan.map((l) => (
            <path
              key={`nen-${l.ten}`}
              d={vanhQuat(R_DU_NIEN_NGOAI, R_DU_NIEN_TRONG, l.goc - 22.5, l.goc + 22.5)}
              style={{ fill: mauNen(l.tot, l.dam) }}
              className="stroke-border"
              strokeWidth={0.8}
            />
          ))}
          {luan.map((l) => (
            <ChuCung
              key={`chu-${l.ten}`}
              id={`dn-${l.goc}`}
              r={(R_DU_NIEN_NGOAI + R_DU_NIEN_TRONG) / 2}
              goc={l.goc}
              doRong={42}
              className="text-[13px] font-bold uppercase tracking-wide"
              style={{ fill: `var(--element-${l.tot ? "wood" : "fire"})` }}
            >
              {l.duNien}
            </ChuCung>
          ))}

          {/* Vành 24 sơn */}
          {SON_24.map((ten, i) => {
            const g = i * 15;
            const laTuDuy = ["Cấn", "Tốn", "Khôn", "Càn"].includes(ten);
            return (
              <g key={ten}>
                <path
                  d={vanhQuat(R_SON_NGOAI, R_SON_TRONG, g - 7.5, g + 7.5)}
                  className={cn("stroke-border", laTuDuy ? "fill-muted/70" : "fill-muted/25")}
                  strokeWidth={0.6}
                />
                <ChuCung
                  id={`son-${i}`}
                  r={(R_SON_NGOAI + R_SON_TRONG) / 2}
                  goc={g}
                  doRong={14}
                  className="fill-foreground/80 text-[10px] font-semibold"
                >
                  {ten}
                </ChuCung>
              </g>
            );
          })}

          {/* Vành 8 hướng */}
          {HUONG_8.map((h) => (
            <g key={h.ten}>
              <path
                d={vanhQuat(R_HUONG_NGOAI, R_HUONG_TRONG, h.goc - 22.5, h.goc + 22.5)}
                className="fill-card stroke-border"
                strokeWidth={0.8}
              />
              <ChuCung
                id={`h-${h.goc}`}
                r={(R_HUONG_NGOAI + R_HUONG_TRONG) / 2}
                goc={h.goc}
                doRong={40}
                className="fill-foreground text-[11px] font-bold"
              >
                {h.ten}
              </ChuCung>
            </g>
          ))}

          {/* Vành bát quái */}
          {HUONG_8.map((h) => (
            <g key={`q-${h.ten}`}>
              <path
                d={vanhQuat(R_QUAI_NGOAI, R_QUAI_TRONG, h.goc - 22.5, h.goc + 22.5)}
                className={cn(
                  "stroke-border",
                  h.quai === menh ? "fill-primary/15" : "fill-muted/20",
                )}
                strokeWidth={0.6}
              />
              <ChuCung
                id={`qt-${h.goc}`}
                r={(R_QUAI_NGOAI + R_QUAI_TRONG) / 2}
                goc={h.goc}
                doRong={38}
                className={cn(
                  "text-[10px] font-semibold",
                  h.quai === menh ? "fill-primary" : "fill-muted-foreground",
                )}
              >
                {h.quai}
              </ChuCung>
            </g>
          ))}

          {/* Viền nhấn cung đang hướng tới */}
          <path
            d={vanhQuat(
              R_DU_NIEN_NGOAI,
              R_QUAI_TRONG,
              huongDangNhin.goc - 22.5,
              huongDangNhin.goc + 22.5,
            )}
            className="fill-none stroke-primary"
            strokeWidth={2.2}
          />
        </g>

        {/* ── Phần cố định: kim chỉ hướng + tâm ── */}
        <line
          x1={C}
          y1={6}
          x2={C}
          y2={394}
          className="stroke-primary/45"
          strokeWidth={1}
          strokeDasharray="4 5"
        />
        <line
          x1={6}
          y1={C}
          x2={394}
          y2={C}
          className="stroke-primary/25"
          strokeWidth={1}
          strokeDasharray="4 5"
        />
        <path d={`M ${C} 2 L ${C - 9} 22 L ${C + 9} 22 Z`} className="fill-primary" />

        <circle
          cx={C}
          cy={C}
          r={R_QUAI_TRONG - 2}
          className="fill-card stroke-primary/35"
          strokeWidth={1.2}
        />
        <text
          x={C}
          y={C - 18}
          textAnchor="middle"
          className="fill-muted-foreground text-[10px] font-semibold uppercase tracking-[0.1em]"
        >
          Cung phi
        </text>
        <text x={C} y={C + 6} textAnchor="middle" className="fill-primary text-[24px] font-bold">
          {menh}
        </text>
        <text x={C} y={C + 24} textAnchor="middle" className="fill-muted-foreground text-[9px]">
          {nhomMenh(menh)}
        </text>
      </svg>

      {/* ── Điều khiển ── */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        {dungCamBien ? (
          <button
            type="button"
            onClick={tatLaBan}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-muted"
          >
            <RotateCcw className="size-3.5" />
            Tắt cảm biến
          </button>
        ) : (
          <button
            type="button"
            onClick={batLaBan}
            disabled={!hoTro || trangThai === "dang-cho"}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition disabled:opacity-45"
          >
            <LocateFixed className="size-3.5" />
            {trangThai === "dang-cho" ? "Đang bắt hướng…" : "Bật la bàn"}
          </button>
        )}
      </div>

      {GHI_CHU_TRANG_THAI[trangThai] ? (
        <p
          className={cn(
            "mt-2 text-center text-[11px] leading-5",
            trangThai === "tu-choi" ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {GHI_CHU_TRANG_THAI[trangThai]}
        </p>
      ) : null}

      {/* Tra tay — máy bàn không có từ kế nên đây mới là cách dùng chính ở đó. */}
      {!dungCamBien ? (
        <div className="mt-3 rounded-xl border border-border/70 bg-muted/25 p-3">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
            <Hand className="size-3.5" />
            Tra tay — kéo mặt la bàn, gạt thanh trượt, hoặc chọn nhanh một hướng
          </p>
          <input
            type="range"
            min={0}
            max={359}
            value={Math.round(gocKeo)}
            onChange={(e) => setGocKeo(Number(e.target.value))}
            aria-label="Chọn góc hướng"
            className="mt-2.5 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-border accent-primary"
          />
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {HUONG_8.map((h) => (
              <button
                key={h.ten}
                type="button"
                onClick={() => setGocKeo(h.goc)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                  huong === h.ten
                    ? "border-primary bg-primary/12 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {h.ten}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Bảng 8 hướng theo cung phi */}
      <ul className="mt-5 divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
        {[...luan]
          .sort((a, b) => Number(b.tot) - Number(a.tot) || b.dam - a.dam)
          .map((l) => (
            <li
              key={l.ten}
              className={cn(
                "flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-xs",
                l.ten === huong && "bg-primary/8",
              )}
            >
              <span className="w-[4.75rem] shrink-0 font-semibold text-foreground">{l.ten}</span>
              <span
                className="w-[5.25rem] shrink-0 font-bold"
                style={{ color: `var(--element-${l.tot ? "wood" : "fire"})` }}
              >
                {l.duNien}
              </span>
              <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{l.muc}</span>
              <span className="order-last w-full min-w-0 text-muted-foreground sm:order-none sm:w-auto sm:flex-1 sm:truncate">
                {l.tomTat}
              </span>
            </li>
          ))}
      </ul>

      <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-5 text-muted-foreground">
        <Compass className="mt-0.5 size-3.5 shrink-0" />
        {canCu ? <span className="font-semibold text-foreground">{canCu} → </span> : null}
        Du niên tính theo phép biến hào từ cung phi {menh} ({nhomMenh(menh)}). Bốn hướng cát là{" "}
        {luan
          .filter((l) => l.tot)
          .map((l) => l.ten)
          .join(", ")}
        .
      </p>
    </div>
  );
}

export { DU_NIEN, DU_NIEN_META };
